/**
 * English + Spanish UI strings. `useT()` returns the typed catalogue for the current
 * language: `const t = useT(); t.common.actions.close`. Strings that take values are
 * functions: `t.common.counter.words(3)`.
 *
 * Add keys to your area's file in i18n/en/ first; TypeScript then asks for the same key in
 * i18n/es/. Names, codes and the brand stay untranslated in the DOM (`translate="no"`).
 *
 * Loading (Chromebook budget, cp-03): only English `common` ships in the entry chunk. Every
 * other area loads on demand, in groups ("bundles"), for the viewer's language only: a
 * student never downloads teacher strings, and nobody downloads a language they don't use.
 * - Routes name the bundles their screens need (app/routes.tsx → `loadMessages`), so the
 *   strings arrive in parallel with the screen's own code.
 * - A Spanish visitor's `common` starts loading as soon as this module runs; the provider
 *   renders nothing until it is ready, so there is never a flash of English.
 * - Switching language loads the same bundles in the new language first, then switches.
 * - Safety net: reading an area that is not loaded yet suspends the component (React
 *   Suspense) until it is, instead of crashing.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { BUNDLES, type Bundle } from './bundles'
import type { Messages } from './en'
import { common as enCommon } from './en/common'
import { LANGS, type Lang } from './types'

export { LANGS, type Lang, type Messages }

export type Namespace = keyof Messages
export { BUNDLES, type Bundle }

// Compile-time check: every area belongs to a bundle (a new area file goes in bundles.ts).
type Unbundled = Exclude<Namespace, (typeof BUNDLES)[Bundle][number]>
const _everyAreaHasABundle: [Unbundled] extends [never] ? true : never = true

const NAMESPACES: readonly Namespace[] = Object.values(BUNDLES).flat()

const BUNDLE_OF = Object.fromEntries(
  Object.entries(BUNDLES).flatMap(([bundle, names]) => names.map((ns) => [ns, bundle])),
) as Record<Namespace, Bundle>

type Loader = () => Promise<unknown>

/**
 * One dynamic import per area file; vite.config.ts groups them into one chunk per language
 * and bundle (i18n-es-learn, …). English `common` is a static import: it is in the entry.
 */
const LOADERS: Record<Lang, Record<Namespace, Loader>> = {
  en: {
    common: () => Promise.resolve(enCommon),
    world: () => import('./en/world').then((m) => m.world),
    reader: () => import('./en/reader').then((m) => m.reader),
    student: () => import('./en/student').then((m) => m.student),
    family: () => import('./en/family').then((m) => m.family),
    hub: () => import('./en/hub').then((m) => m.hub),
    teacher: () => import('./en/teacher').then((m) => m.teacher),
    teacherWalk: () => import('./en/teacherWalk').then((m) => m.teacherWalk),
    projector: () => import('./en/projector').then((m) => m.projector),
    public: () => import('./en/public').then((m) => m.publicPages),
    museum: () => import('./en/museum').then((m) => m.museum),
    office: () => import('./en/office').then((m) => m.office),
  },
  es: {
    common: () => import('./es/common').then((m) => m.common),
    world: () => import('./es/world').then((m) => m.world),
    reader: () => import('./es/reader').then((m) => m.reader),
    student: () => import('./es/student').then((m) => m.student),
    family: () => import('./es/family').then((m) => m.family),
    hub: () => import('./es/hub').then((m) => m.hub),
    teacher: () => import('./es/teacher').then((m) => m.teacher),
    teacherWalk: () => import('./es/teacherWalk').then((m) => m.teacherWalk),
    projector: () => import('./es/projector').then((m) => m.projector),
    public: () => import('./es/public').then((m) => m.publicPages),
    museum: () => import('./es/museum').then((m) => m.museum),
    office: () => import('./es/office').then((m) => m.office),
  },
}

// ---------------------------------------------------------------------------------------------
// The registry: which areas are loaded, per language. Shared by every provider on the page.

const loaded: Record<Lang, Partial<Record<Namespace, unknown>>> = {
  en: { common: enCommon },
  es: {},
}
const inflight = new Map<string, Promise<void>>()
const failures = new Map<string, unknown>()
/** Bundles some route asked for; a language switch loads these in the new language first. */
const wanted = new Set<Bundle>(['core'])
const listeners = new Set<() => void>()
let version = 0

function notify(): void {
  version += 1
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getVersion = () => version

function isBundleLoaded(lang: Lang, bundle: Bundle): boolean {
  return BUNDLES[bundle].every((ns) => loaded[lang][ns] !== undefined)
}

function loadBundle(lang: Lang, bundle: Bundle): Promise<void> {
  if (isBundleLoaded(lang, bundle)) return Promise.resolve()
  const key = `${lang}:${bundle}`
  let promise = inflight.get(key)
  if (!promise) {
    failures.delete(key)
    promise = Promise.all(
      BUNDLES[bundle].map(async (ns) => {
        loaded[lang][ns] ??= await LOADERS[lang][ns]()
      }),
    ).then(
      () => {
        inflight.delete(key)
        notify()
      },
      (error: unknown) => {
        inflight.delete(key)
        failures.set(key, error)
        throw error
      },
    )
    inflight.set(key, promise)
  }
  return promise
}

function isReady(lang: Lang, bundles: Iterable<Bundle>): boolean {
  for (const bundle of bundles) if (!isBundleLoaded(lang, bundle)) return false
  return true
}

function loadAll(lang: Lang, bundles: Iterable<Bundle>): Promise<void> {
  return Promise.all([...bundles].map((b) => loadBundle(lang, b))).then(() => undefined)
}

export const LANG_STORAGE_KEY = 'galley.lang'

function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value)
}

/** Stored choice first, then the browser language (Spanish if it starts with "es"). */
export function detectLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY)
    if (isLang(stored)) return stored
  } catch {
    // Storage may be blocked (private mode, managed devices); fall back to the browser.
  }
  return navigator.language.toLowerCase().startsWith('es') ? 'es' : 'en'
}

/** The language new route loads are for: the last one a provider showed or was asked for. */
let activeLang: Lang = detectLang()
// Start the viewer's `common` now, in parallel with the rest of the app's start-up.
if (!isBundleLoaded(activeLang, 'core')) loadBundle(activeLang, 'core').catch(() => undefined)

/**
 * Load the string bundles a screen needs, in the current language. Route loaders call this
 * next to the screen's own import, so both arrive together.
 */
export function loadMessages(bundles: readonly Bundle[]): Promise<void> {
  for (const bundle of bundles) wanted.add(bundle)
  return loadAll(activeLang, bundles)
}

/** Tests, and the component gallery: every area in every language, loaded now. */
export function loadAllMessages(): Promise<void> {
  const all = Object.keys(BUNDLES) as Bundle[]
  return Promise.all(LANGS.map((lang) => loadAll(lang, all))).then(() => undefined)
}

// Unit tests render screens without routes: load everything before any test runs.
if (import.meta.env.MODE === 'test') await loadAllMessages()

/**
 * A catalogue whose areas are read from the registry on access. An area that is not loaded
 * yet suspends the reading component until it is (and throws if it failed to load, so the
 * nearest error boundary offers a reload). `_version` only keys the memo: a new object each
 * time areas arrive, so memoized readers re-render with them.
 */
function catalogue(lang: Lang, _version: number): Messages {
  const t = {} as Messages
  for (const ns of NAMESPACES) {
    Object.defineProperty(t, ns, {
      enumerable: true,
      get() {
        const value = loaded[lang][ns]
        if (value !== undefined) return value
        const bundle = BUNDLE_OF[ns]
        const failure = failures.get(`${lang}:${bundle}`)
        if (failure !== undefined && !inflight.has(`${lang}:${bundle}`)) throw failure
        if (import.meta.env.DEV) {
          console.warn(
            `i18n: "${ns}" was read before its bundle "${bundle}" loaded. Add it to the route in app/routes.tsx.`,
          )
        }
        wanted.add(bundle)
        // Suspense: React shows the nearest fallback and retries once the strings are here.
        throw loadBundle(lang, bundle)
      },
    })
  }
  return t
}

interface I18nContextValue {
  lang: Lang
  setLang: (lang: Lang) => void
  t: Messages
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({
  children,
  initialLang,
}: {
  children: ReactNode
  initialLang?: Lang
}) {
  const [lang, setLangState] = useState<Lang>(() => initialLang ?? activeLang)
  const registryVersion = useSyncExternalStore(subscribe, getVersion, getVersion)
  // The language asked for last: a slow load must not undo a later choice.
  const requested = useRef(lang)
  const coreReady = loaded[lang].common !== undefined

  const setLang = useCallback((next: Lang) => {
    requested.current = next
    activeLang = next
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next)
    } catch {
      // The choice still applies for this visit.
    }
    // Everything on screen switches at once: load the same bundles in the new language first.
    if (isReady(next, wanted)) {
      setLangState(next)
      return
    }
    loadAll(next, wanted).then(
      () => {
        if (requested.current === next) setLangState(next)
      },
      () => {
        // Offline or a failed chunk: stay in the language that works. A later click retries.
        if (requested.current !== next) return
        setLangState((current) => {
          requested.current = current
          activeLang = current
          return current
        })
      },
    )
  }, [])

  useEffect(() => {
    activeLang = lang
    document.documentElement.lang = lang
  }, [lang])

  useEffect(() => {
    if (coreReady) return
    let live = true
    // A language whose strings can't load (offline on first visit) falls back to English,
    // which is always in the entry chunk.
    loadBundle(lang, 'core').catch(() => {
      if (live) setLangState('en')
    })
    return () => {
      live = false
    }
  }, [lang, coreReady])

  const t = useMemo(() => catalogue(lang, registryVersion), [lang, registryVersion])
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t])
  // Nothing renders until the viewer's language is ready: no flash of the wrong language.
  if (!coreReady) return null
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useT/useLang must be used inside <I18nProvider>')
  return ctx
}

export function useT(): Messages {
  return useI18n().t
}

export function useLang(): { lang: Lang; setLang: (lang: Lang) => void } {
  const { lang, setLang } = useI18n()
  return { lang, setLang }
}
